'use strict';

// Dependencies
const Backbone = require('dibs-backbone');
const SoaModel = require('bunsen/models/SoaModel');
const _ = require('underscore');
const assert = require('bunsen/helpers/assert');
let SolrSearchModel;

/**
 * @class SolrSearchModel
 * @extends SoaModel
 */

SolrSearchModel = module.exports = SoaModel.extend({
    initialize: function (attrs, options) {
        assert(options.collectionName, 'You must include a name for the collection we are going to fetch.');
        assert(options.collectionType, 'You must include the type for the collection we are going to fetch.');
        assert(options.paramsMap, 'You must include a map for converting parameters.');
        assert(options.queryTemplate, 'You must include a query template name for us to fetch data.');
        this.collectionName = options.collectionName;
        this.CollectionType = options.collectionType;
        this.queryTemplate = options.queryTemplate;
        this.paramsMap = options.paramsMap || {};
        this.facetParamsMap = options.facetParamsMap || {};
        this.pageSize = _.isNumber(options.pageSize) ? options.pageSize : 48;
        this.pageStart = options.pageStart || 0;
        this.context = options.context || 'ADMIN';
        this.availableActions = options.availableActions;
        this.baseParams = options.baseParams || ['queryTemplate', 'pageSize', 'pageStart', 'context'];
        this.set(this.collectionName, options[this.collectionName] || new this.CollectionType());
        this.query = new Backbone.Model();
        this.resetParams();
    },

    fetch: function (options) {
        // pass query params as part of fetch
        options = _.extend({data: this.query.toJSON()}, options, {traditional: true});
        return SolrSearchModel.__super__.fetch.call(this, options);
    },

    parse: function (data) {
        const collection = this.getCollection();
        if (collection) {
            this.getCollection().reset(data.result[this.collectionName], {parse : true});
        }

        // Total size of paginated list.
        // We are adding as a property AND attribute for backwards compatibility
        if (_.isNumber(data.totalResults)) {
            this.totalResults = data.totalResults;
        }

        return {
            facets: data.result.facets,
            totalResults: data.totalResults,
            pageSize: data.pageSize,
            pageStart: data.pageStart
        };
    },

    /**
     * Helper function to get collection data.
     * @public
     */
    getCollection: function () {
        return this.get(this.collectionName);
    },

    /**
     * Add parameters to this.query.
     * @param key - name of parameter
     * @param val - value of parameter
     * @param options - extra options, for params with multiple values
     * @public
     */
    setParam : function (key, val, options) {
        const convertedKey = this.paramsMap[key] || key;
        const currentVal = this.query.get(convertedKey);

        options = options || {};

        if (options.append && currentVal && !_.contains(this.baseParams, key)) {
            return this.query.set(convertedKey, _.uniq(_.flatten([currentVal, val])));
        } else if (val !== undefined) {
            return this.query.set(convertedKey, val);
        }
    },

    /**
     * Remove param/key pair from this.query.
     * @param key - name of parameter
     * @param val - value of parameter. If omitted, just remove param.
     * @public
     */
    removeParam: function (key, val) {
        let currentVal;
        const convertedKey = this.paramsMap[key] || key;

        if (val) {
            currentVal = this.query.get(convertedKey);

            // if not array and val is there, remove param entirely
            if (!_.isArray(currentVal)) {
                if (currentVal === val) {
                    this.query.unset(convertedKey);
                }
            } else { // if array and val is there, take out of array
                currentVal = _.filter(currentVal, function (indexVal) {
                    return indexVal !== val;
                });

                // remove if array now empty
                if (!currentVal.length) {
                    this.query.unset(convertedKey);
                } else {
                    this.query.set(convertedKey, currentVal);
                }
            }
        } else { // if val is undefined, remove param entirely
            this.query.unset(convertedKey);
        }
    },

    /**
     * Convenience method for setting many params at once from an object.
     * @param params
     * @param options
     */
    setParams : function (params, options) {
        _.each(params, (vals, key) => {
            // if array of values, add each
            if (_.isArray(vals)) {
                _.each(vals, val => {
                    this.setParam(key, val, options);
                });
            } else { // not array, just add value
                this.setParam(key, vals, options);
            }
        });
    },

    /**
     * Convenience method for getting a param from the query object.
     * @param key
     */
    getParam : function (key) {
        const convertedKey = this.paramsMap[key] || key;
        return this.query.get(convertedKey);
    },

    /**
     * Get all params with keys converted.
     */
    getCurrentParams : function () {
        const results = {};
        const invertedMap = _.invert(this.paramsMap);

        _.each(this.query.toJSON(), (val, key) => {
            results[invertedMap[key] || key] = val; // push values to return object
        });

        return results;
    },

    /**
     * Convenience method for clearing (most) params and (optionally) setting new ones.
     * @param params
     * @param options
     */
    resetParams : function (newParams, options) {
        // clear all params
        this.query.clear();

        // set baseline params, which should all be singletons
        _.each(this.baseParams, param => {
            this.setParam(param, this[param]);
        });

        // add possible new params
        this.setParams(newParams, options);
    },

    /**
     * Filter params by key and value.
     * @param key
     * @param val
     * @returns boolean
     */
    filterParam : function (key, val) {
        const storedVal = this.getParam(key);

        if (!_.isArray(storedVal)) {
            return storedVal === val;
        } else {
            return (_.contains(storedVal, val));
        }
    },

    getFacets: function () {
        return this.get('facets');
    }
});



// WEBPACK FOOTER //
// ./~/bunsen/models/SolrSearchModel.es.js